import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { fetchFeed, returnToMerchant, sendFile, sendMessage } from '../../services/conciergeChat';
import { openChatSocket, ChatSocketState } from '../../services/conciergeSocket';
import type { ChatFeed } from '../../services/conciergeChatTypes';
import type { Transaction } from '../../services/types';
import { isVideoFile } from '../../services/conciergeVideo';
import { uuidv4 } from '../../analytics/identity';
import { buildRows } from './feedModel';
import IssuanceCard from './IssuanceCard';
import Composer from './Composer';
import { money } from './tokens';
import {
  BackButton,
  ChatHeader,
  EventRow,
  FeedBox,
  FeedFill,
  Frame,
  MessageBubble,
  NoticeRow,
  PendingBubble,
  Plaque,
  QrReceiptRow,
  StateRow,
} from './ui';

const RECEIPT_PARAM = 'receipt';

/**
 * A message or a file drawn the moment the client sends it, until the feed brings it back.
 * `seen` counts what the feed held when it was sent (plus earlier pending ones of the same kind):
 * client messages with this text, or — for a file — client messages with an attachment and «чек
 * получен» rows. Once the feed holds more, it has arrived and the bubble gives way to it.
 */
interface Pending {
  key: number;
  text: string;
  file: File | null;
  /** An object URL of the picked image or video, for its preview. */
  preview: string | null;
  progress: number | null;
  failed: boolean;
  /** The previous video of this ticket is still on its way: said, so the client waits for it. */
  busy: boolean;
  seen: { copies: number; attachments: number; receipts: number };
  /** A video: found in the feed by its upload tag, never by the count other files use. */
  video: boolean;
  /** Every attempt's upload id: the video is in the feed once a message carries one of them as its tag. */
  attempts: string[];
}

/** The backend tags the client's video message with its upload id. */
const UPLOAD_TAG = 'upload:';
const isUploadTagged = (m: ChatFeed['messages'][number]): boolean => m.tags.some((tag) => tag.startsWith(UPLOAD_TAG));

const counts = (feed: ChatFeed | null, text: string) => {
  const client = feed ? feed.messages.filter((m) => m.from === 'client') : [];
  return {
    copies: client.filter((m) => m.text === text).length,
    // Videos are found by their tag, so they never move the count the other files rely on.
    attachments: client.filter((m) => m.attachments.length > 0 && !isUploadTagged(m)).length,
    receipts: feed ? feed.messages.filter((m) => m.tags.some((tag) => tag.startsWith('chc:receipt'))).length : 0,
  };
};

const arrived = (feed: ChatFeed | null, m: Pending): boolean => {
  if (m.video) {
    return Boolean(feed?.messages.some((msg) => msg.tags.some((tag) => m.attempts.some((id) => tag === `${UPLOAD_TAG}${id}`))));
  }
  const now = counts(feed, m.text);
  return m.file ? now.attachments > m.seen.attachments || now.receipts > m.seen.receipts : now.copies > m.seen.copies;
};

const isImage = (file: File): boolean => file.type.startsWith('image/');
/** A computer, by how it is used rather than by its width: a phone in landscape or a tablet is not one. */
const DESKTOP = '(hover: hover) and (pointer: fine)';

const isDesktop = (): boolean => {
  try {
    return window.matchMedia(DESKTOP).matches;
  } catch {
    return false;
  }
};

/** The same page with the mark that brings the phone straight to the attach button. */
const phoneUrl = (): string => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(RECEIPT_PARAM, '1');
    return url.toString();
  } catch {
    return window.location.href;
  }
};

const cameForReceipt = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).get(RECEIPT_PARAM) === '1';
  } catch {
    return false;
  }
};

/**
 * The concierge deposit's page: a chat with the operator. It lives on the feed the backend answers
 * and re-reads it on every socket signal and every reconnect — there is no polling and no timer of
 * the page's own; the deposit's outcome is the plaque above the feed. Russian only.
 */
const ConciergeChat = ({ uuid, transaction, onRedirect }: { uuid: string; transaction: Transaction; onRedirect: (url: string) => void }) => {
  const { i18n } = useTranslation();
  const t = useMemo(() => i18n.getFixedT('ru'), [i18n]);
  const [feed, setFeed] = useState<ChatFeed | null>(null);
  const [failed, setFailed] = useState(false);
  const [socket, setSocket] = useState<ChatSocketState>('connected');
  const [desktop] = useState(isDesktop);
  const [leaving, setLeaving] = useState(false);
  const [pending, setPending] = useState<Pending[]>([]);
  const pendingKey = useRef(1);
  const uploadSeq = useRef(1);
  const latestFeed = useRef<ChatFeed | null>(null);
  latestFeed.current = feed;
  const feedBox = useRef<HTMLElement>(null);
  const atBottom = useRef(true);
  const reading = useRef<Promise<void> | null>(null);
  const again = useRef(false);

  // One read at a time; a signal that arrives meanwhile asks for exactly one more.
  const load = useCallback(async () => {
    if (reading.current) {
      again.current = true;
      return reading.current;
    }
    reading.current = (async () => {
      do {
        again.current = false;
        try {
          setFeed(await fetchFeed(uuid));
          setFailed(false);
        } catch (err) {
          setFailed(true);
          Sentry.captureException({ target: 'concierge.feed', err });
        }
      } while (again.current);
    })();
    await reading.current;
    reading.current = null;
  }, [uuid]);

  useEffect(() => {
    void load();
    return openChatSocket(uuid, { onChange: () => void load(), onState: setSocket });
  }, [uuid, load]);

  // A finished deposit of a player who has a live one: the conversation goes on there.
  // Once per token: the page's redirect handler is not stable, and the navigation takes a moment.
  // The query string (`receipt=1`) is dropped on purpose: a file picked on the live deposit becomes
  // its receipt, so a late receipt of this one goes through the operator.
  const jumpTo = feed?.jumpTo ?? null;
  const jumped = useRef<string | null>(null);
  useEffect(() => {
    if (!jumpTo || jumpTo === uuid || jumped.current === jumpTo) return;
    jumped.current = jumpTo;
    onRedirect(`/${jumpTo}`);
  }, [jumpTo, uuid, onRedirect]);

  const keepBottom = useCallback(() => {
    const box = feedBox.current;
    if (box && atBottom.current) box.scrollTop = box.scrollHeight;
  }, []);

  const rows = useMemo(() => (feed ? buildRows(feed, { desktop }) : []), [feed, desktop]);
  useLayoutEffect(keepBottom, [rows, pending, keepBottom]);

  const onScroll = () => {
    const box = feedBox.current;
    if (box) atBottom.current = box.scrollHeight - box.scrollTop - box.clientHeight < 40;
  };

  // The socket signal usually re-reads the feed before the send answers, so the bubble is hidden by
  // the feed (by `seen`, where it is rendered) and only dropped here; it never shows next to its own
  // message.
  const patch = (key: number, change: Partial<Pending>) =>
    setPending((list) => list.map((m) => (m.key === key ? { ...m, ...change } : m)));

  const drop = (key: number) =>
    setPending((list) => {
      const done = list.find((p) => p.key === key);
      if (done?.preview) URL.revokeObjectURL(done.preview);
      return list.filter((p) => p.key !== key);
    });

  const deliver = async (m: Pick<Pending, 'key' | 'text' | 'file' | 'video'>) => {
    // A new id per attempt: the answer to an earlier one may still land in the feed.
    // Time and the attempt's number keep it unique where the browser has no WebCrypto and `uuidv4`
    // is not random.
    const uploadId = m.video ? `${uuidv4()}-${Date.now().toString(36)}-${uploadSeq.current++}` : null;
    setPending((list) =>
      list.map((p) =>
        p.key === m.key ? { ...p, failed: false, busy: false, attempts: uploadId ? [...p.attempts, uploadId] : p.attempts, progress: m.file ? 0 : null } : p,
      ),
    );
    try {
      if (m.file) await sendFile(uuid, m.file, (share) => patch(m.key, { progress: share }), uploadId ?? undefined);
      else await sendMessage(uuid, m.text);
      await load();
      drop(m.key);
    } catch (err) {
      const busy = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code === 'CONCIERGE_VIDEO_IN_PROGRESS';
      patch(m.key, { failed: true, busy, progress: null });
    }
  };

  // A video the feed now holds has arrived — even one already given up on: its bubble and preview go.
  useEffect(() => {
    pending.filter((p) => p.video && arrived(feed, p)).forEach((p) => drop(p.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, pending]);

  const enqueue = (text: string, file: File | null) => {
    const key = pendingKey.current++;
    const video = file ? isVideoFile(file) : false;
    atBottom.current = true;
    setPending((list) => {
      const base = counts(latestFeed.current, text);
      const sameText = list.filter((p) => !p.file && p.text === text).length;
      const files = list.filter((p) => p.file && !p.video).length;
      const seen = file
        ? { copies: base.copies, attachments: base.attachments + files, receipts: base.receipts + files }
        : { ...base, copies: base.copies + sameText };
      const preview = file && (isImage(file) || video) ? URL.createObjectURL(file) : null;
      return [...list, { key, text, file, preview, progress: file ? 0 : null, failed: false, busy: false, seen, video, attempts: [] }];
    });
    void deliver({ key, text, file, video });
  };

  const onSend = (text: string) => enqueue(text, null);
  const onFile = (file: File) => enqueue('', file);

  const onBack = async () => {
    setLeaving(true);
    let target = transaction.redirectUrl ?? null;
    try {
      const answer = await returnToMerchant(uuid);
      target = answer.redirectUrl ?? target;
    } catch (err) {
      Sentry.captureException({ target: 'concierge.return', err });
    }
    if (target) window.location.replace(target);
    else setLeaving(false);
  };

  const ticket = feed?.ticket;
  const view = ticket?.view ?? 'none';
  const input = ticket?.input ?? 'open';
  const status =
    view === 'expired' || view === 'expired_with_hint'
      ? t('concierge.chat.status.expired')
      : view === 'closed'
        ? t('concierge.chat.status.closed')
        : view === 'completed'
          ? t('concierge.chat.status.completed')
          : socket === 'reconnecting'
          ? t('concierge.chat.status.reconnecting')
          : ticket?.operatorName
            ? t('concierge.chat.status.online', { name: ticket.operatorName })
            : ticket?.operatorOnline
              ? t('concierge.chat.status.onlineAnonymous')
              : t('concierge.chat.status.searching');
  const credited = ticket?.creditedAmount ?? ticket?.amount ?? null;
  const online = view === 'none' ? Boolean(ticket?.operatorOnline || ticket?.operatorName) && socket === 'connected' : null;

  const renderRow = (row: (typeof rows)[number], i: number) => {
    const previous = rows[i - 1];
    switch (row.type) {
      case 'message':
        return (
          <MessageBubble
            key={row.key}
            message={row.message}
            author={t('concierge.chat.author')}
            fileLabel={t('concierge.chat.file')}
            showAuthor={row.message.from !== 'client' && !(previous?.type === 'message' && previous.message.from !== 'client')}
          />
        );
      case 'card':
        return <IssuanceCard key={row.key} issuance={row.issuance} receiptSent={row.receiptSent} deadlineAt={ticket?.deadlineAt ?? null} t={t} />;
      case 'event':
        return <EventRow key={row.key} kind={row.kind} text={row.text} />;
      case 'qr':
        return <QrReceiptRow key={row.key} url={phoneUrl()} title={t('concierge.chat.qr.title')} text={t('concierge.chat.qr.text')} />;
      case 'state':
        return <StateRow key={row.key} state={row.state} text={t(`concierge.chat.state.${row.state}`)} />;
      default:
        return null;
    }
  };
  // Pending bubbles go after the conversation and before the transient state row.
  const cut = rows.findIndex((row) => row.type === 'state');
  const split = cut < 0 ? rows.length : cut;

  return (
    <Frame data-testid='concierge-chat'>
      <ChatHeader
        title={t('concierge.chat.title')}
        amount={ticket?.amount != null ? money(ticket.amount) : null}
        status={status}
        online={online}
      />

      {view === 'completed' && ticket && (
        <Plaque
          tone='ok'
          title={credited != null ? t('concierge.chat.plaque.completed', { amount: money(credited) }) : t('concierge.chat.plaque.completed', { amount: '' }).trim()}
          text={t('concierge.chat.plaque.completedHint')}
        />
      )}
      {(view === 'expired' || view === 'expired_with_hint') && (
        <Plaque tone='late' title={t('concierge.chat.plaque.expired')} text={view === 'expired_with_hint' ? t('concierge.chat.plaque.expiredHint') : null} />
      )}
      {view === 'closed' && <Plaque tone='neutral' title={t('concierge.chat.plaque.closed')} text={null} />}

      <FeedBox ref={feedBox} onScroll={onScroll}>
        <FeedFill />
        {failed && !feed && <NoticeRow text={t('concierge.chat.loadError')} />}
        {feed && !feed.messagesAvailable && <NoticeRow text={t('concierge.chat.messagesUnavailable')} />}
        {rows.slice(0, split).map((row, i) => renderRow(row, i))}
        {pending.filter((m) => !arrived(feed, m)).map((m) => (
          <PendingBubble
            key={`p-${m.key}`}
            text={m.text}
            fileName={m.file?.name ?? null}
            preview={m.preview}
            failed={m.failed}
            sendingLabel={
              m.progress !== null && m.progress > 0
                ? `${t('concierge.chat.delivery.sending')} ${Math.min(99, Math.round(m.progress * 100))}%`
                : t('concierge.chat.delivery.sending')
            }
            video={m.video}
            failedLabel={m.busy ? t('concierge.chat.delivery.videoBusy') : t('concierge.chat.delivery.failed')}
            retryLabel={t('concierge.chat.delivery.retry')}
            onRetry={() => void deliver(m)}
          />
        ))}
        {rows.slice(split).map((row, i) => renderRow(row, split + i))}
      </FeedBox>

      {input !== 'closed' && (
        <Composer
          input={input}
          uuid={uuid}
          transaction={transaction}
          t={t}
          desktop={desktop}
          disabled={!feed || !feed.messagesAvailable}
          phoneUrl={phoneUrl()}
          focusAttach={cameForReceipt()}
          onSend={onSend}
          onFile={onFile}
          onResize={keepBottom}
        />
      )}
      <BackButton type='button' $primary={view === 'completed'} disabled={leaving} onClick={() => void onBack()}>
        {t('concierge.chat.back')}
      </BackButton>
    </Frame>
  );
};

export default ConciergeChat;
